import React from 'react';
import { motion } from 'framer-motion';
import { Flower, Sun, Leaf, Home, BookOpen, Heart, Utensils, Sparkles, Users, Wifi, Coffee, Wind } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
const mainFacilities = [{
  icon: Sun,
  title: 'Meditation Halls',
  description: 'Three spacious meditation halls designed for deep practice',
  image: 'https://images.unsplash.com/photo-1544367563-12123d8965cd?q=80&w=2070&auto=format&fit=crop',
  features: ['Capacity: 100, 50, and 30 practitioners', 'Natural lighting with adjustable ambiance', 'Acoustic excellence for sound meditation', 'Temperature-controlled environment', 'Cushions, mats, and props provided', 'Wheelchair accessible']
}, {
  icon: Leaf,
  title: 'Sacred Gardens',
  description: 'Beautifully landscaped grounds for walking meditation and contemplation',
  image: 'https://images.unsplash.com/photo-1519834785169-98be25ec3f84?q=80&w=1964&auto=format&fit=crop',
  features: ['50 acres of pristine natural beauty', 'Lotus ponds and water features', 'Meditation groves and quiet zones', 'Labyrinth for walking meditation', 'Organic herb and flower gardens', 'Sunset viewing platforms']
}, {
  icon: Home,
  title: 'Accommodations',
  description: 'Comfortable rooms designed for rest and reflection',
  image: 'https://images.unsplash.com/photo-1631049307264-da0ec9d70304?q=80&w=2070&auto=format&fit=crop',
  features: ['Shared rooms (2-4 people)', 'Private rooms (limited availability)', 'Simple, clean, minimalist design', 'Natural materials and calming colors', 'Bedding and towels provided', 'Quiet hours respected']
}, {
  icon: Utensils,
  title: 'Dining Hall',
  description: 'Communal space serving fresh, organic vegetarian meals',
  image: 'https://images.unsplash.com/photo-1555939594-58d7cb561ad1?q=80&w=1887&auto=format&fit=crop',
  features: ['Three sattvic meals daily', 'Organic ingredients from our farm', 'Accommodates dietary restrictions', 'Silent meal options available', 'Indoor and outdoor seating', 'Tea and filtered water always available']
}, {
  icon: Heart,
  title: 'Wellness Center',
  description: 'Holistic health services for body, mind, and spirit',
  image: 'https://images.unsplash.com/photo-1544161515-4ab6ce6db874?q=80&w=2070&auto=format&fit=crop',
  features: ['Ayurvedic consultations', 'Massage and bodywork', 'Energy healing sessions', 'Yoga therapy', 'Herbal medicine dispensary', 'Private treatment rooms']
}, {
  icon: BookOpen,
  title: 'Library & Study Rooms',
  description: 'Extensive collection of spiritual texts and quiet study spaces',
  image: 'https://images.unsplash.com/photo-1521587760476-6c12a4b040da?q=80&w=2070&auto=format&fit=crop',
  features: ['5,000+ books on meditation and spirituality', 'Audio and video teachings', 'Private study alcoves', 'Group discussion rooms', 'Research assistance available', 'Lending library for residents']
}];
const additionalAmenities = [{
  icon: Wifi,
  name: 'Limited WiFi',
  description: 'Available in common areas only'
}, {
  icon: Coffee,
  name: 'Tea Lounge',
  description: 'Herbal teas and light refreshments'
}, {
  icon: Wind,
  name: 'Yoga Pavilion',
  description: 'Open-air space for asana practice'
}, {
  icon: Sparkles,
  name: 'Fire Circle',
  description: 'Evening gatherings and ceremonies'
}, {
  icon: Users,
  name: 'Community Hall',
  description: 'Workshops and group activities'
}, {
  icon: Flower,
  name: 'Meditation Caves',
  description: 'Individual practice spaces'
}];
const guidelines = [{
  title: 'Quiet Hours',
  description: 'Silence is maintained from 9 PM to 7 AM throughout the campus to support deep rest and early morning practice.'
}, {
  title: 'Dress Code',
  description: 'Modest, comfortable clothing is requested. White or light colors are encouraged for meditation sessions.'
}, {
  title: 'Technology',
  description: 'We encourage digital detox. Mobile phones should be kept on silent and used only in designated areas.'
}, {
  title: 'Alcohol & Substances',
  description: 'Our campus is completely substance-free, including alcohol, tobacco, and recreational drugs.'
}, {
  title: 'Environmental Care',
  description: 'We practice sustainability. Please conserve water, minimize waste, and respect all living beings.'
}, {
  title: 'Sacred Space',
  description: 'Remove shoes before entering meditation halls. Maintain silence in designated quiet zones.'
}];
export function FacilitiesPage() {
  return <div className="min-h-screen bg-[#FFFBF5]" data-id="element-694">
      <Header data-id="element-695" />

      {/* Hero Section */}
      <section className="relative h-[70vh] min-h-[600px] w-full overflow-hidden" data-id="element-696">
        <motion.div initial={{
        scale: 1.1,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: 1
      }} transition={{
        duration: 1.2,
        ease: 'easeOut'
      }} className="absolute inset-0" data-id="element-697">
          <img src="https://images.unsplash.com/photo-1506126613408-eca07ce68773?q=80&w=2070&auto=format&fit=crop" alt="Meditation center facilities" className="w-full h-full object-cover" data-id="element-698" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-[#FFFBF5]" data-id="element-699" />
        </motion.div>

        <div className="absolute bottom-0 left-0 right-0 pb-16" data-id="element-700">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-701">
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: 0.3
          }} className="max-w-4xl" data-id="element-702">
              <h1 className="font-serif text-5xl md:text-7xl text-white mb-6 leading-tight drop-shadow-2xl" data-id="element-703">
                Our Facilities
              </h1>
              <p className="text-xl md:text-2xl text-white/95 leading-relaxed drop-shadow-lg" data-id="element-704">
                A sanctuary designed for transformation, where every space
                supports your journey inward
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className="py-20 bg-white" data-id="element-705">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-706">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="text-center mb-16" data-id="element-707">
            <h2 className="font-serif text-4xl text-[#2C3E50] mb-6" data-id="element-708">
              A Space for Deep Practice
            </h2>
            <p className="text-lg text-[#5D6D7E] leading-relaxed max-w-3xl mx-auto" data-id="element-709">
              Our 50-acre campus has been thoughtfully designed to create an
              environment conducive to meditation, self-inquiry, and spiritual
              growth. Every facility reflects our commitment to simplicity,
              sustainability, and the sacred.
            </p>
          </motion.div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8" data-id="element-710">
            {[{
            number: '50',
            label: 'Acres'
          }, {
            number: '3',
            label: 'Meditation Halls'
          }, {
            number: '80',
            label: 'Guest Rooms'
          }, {
            number: '200',
            label: 'Capacity'
          }].map((stat, index) => <motion.div key={index} initial={{
            opacity: 0,
            scale: 0.9
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: index * 0.1
          }} className="text-center" data-id="element-711">
                <div className="font-serif text-5xl font-bold text-[#8B9D83] mb-2" data-id="element-712">
                  {stat.number}
                </div>
                <div className="text-[#5D6D7E]" data-id="element-713">{stat.label}</div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Main Facilities */}
      <section className="py-20 bg-[#FAF7F2]" data-id="element-714">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-715">
          <div className="space-y-24" data-id="element-716">
            {mainFacilities.map((facility, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-100px'
          }} transition={{
            duration: 0.8
          }} className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`} data-id="element-717">
                {/* Image */}
                <div className={`${index % 2 === 1 ? 'lg:order-2' : ''}`} data-id="element-718">
                  <div className="relative aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl" data-id="element-719">
                    <img src={facility.image} alt={facility.title} className="w-full h-full object-cover" data-id="element-720" />
                    <div className="absolute top-6 left-6 w-16 h-16 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center shadow-lg" data-id="element-721">
                      <facility.icon className="w-8 h-8 text-[#8B9D83]" data-id="element-722" />
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className={`${index % 2 === 1 ? 'lg:order-1' : ''}`} data-id="element-723">
                  <h3 className="font-serif text-4xl text-[#2C3E50] mb-4" data-id="element-724">
                    {facility.title}
                  </h3>
                  <p className="text-lg text-[#5D6D7E] mb-8 leading-relaxed" data-id="element-725">
                    {facility.description}
                  </p>
                  <ul className="space-y-3" data-id="element-726">
                    {facility.features.map((feature, i) => <li key={i} className="flex items-start gap-3" data-id="element-727">
                        <div className="w-2 h-2 rounded-full bg-[#8B9D83] mt-2 flex-shrink-0" data-id="element-728" />
                        <span className="text-[#5D6D7E]" data-id="element-729">{feature}</span>
                      </li>)}
                  </ul>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Additional Amenities */}
      <section className="py-20 bg-white" data-id="element-730">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-731">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-732">
            <h2 className="font-serif text-4xl text-[#2C3E50] mb-4 text-center" data-id="element-733">
              Additional Amenities
            </h2>
            <p className="text-center text-[#5D6D7E] mb-12 max-w-2xl mx-auto" data-id="element-734">
              Everything you need for a comfortable and transformative retreat
              experience
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-735">
              {additionalAmenities.map((amenity, index) => <motion.div key={index} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: index * 0.1
            }} className="bg-[#FAF7F2] p-6 rounded-xl" data-id="element-736">
                  <div className="w-12 h-12 rounded-full bg-[#8B9D83]/20 flex items-center justify-center mb-4" data-id="element-737">
                    <amenity.icon className="w-6 h-6 text-[#8B9D83]" data-id="element-738" />
                  </div>
                  <h3 className="font-serif text-xl font-bold text-[#2C3E50] mb-2" data-id="element-739">
                    {amenity.name}
                  </h3>
                  <p className="text-[#5D6D7E]" data-id="element-740">{amenity.description}</p>
                </motion.div>)}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Guidelines */}
      <section className="py-20 bg-[#FAF7F2]" data-id="element-741">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-742">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-743">
            <h2 className="font-serif text-4xl text-[#2C3E50] mb-4 text-center" data-id="element-744">
              Campus Guidelines
            </h2>
            <p className="text-center text-[#5D6D7E] mb-12 max-w-2xl mx-auto" data-id="element-745">
              Simple guidelines to maintain the sacred atmosphere and support
              everyone's practice
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-746">
              {guidelines.map((guideline, index) => <motion.div key={index} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: index * 0.1
            }} className="bg-white p-6 rounded-xl shadow-md" data-id="element-747">
                  <h3 className="font-serif text-xl font-bold text-[#2C3E50] mb-3" data-id="element-748">
                    {guideline.title}
                  </h3>
                  <p className="text-[#5D6D7E] leading-relaxed" data-id="element-749">
                    {guideline.description}
                  </p>
                </motion.div>)}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Virtual Tour CTA */}
      <section className="py-20 bg-white" data-id="element-750">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-751">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="bg-gradient-to-br from-[#8B9D83] to-[#7A8C72] rounded-3xl p-12 text-center text-white relative overflow-hidden" data-id="element-752">
            {/* Decorative elements */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full blur-3xl" data-id="element-753" />
            <div className="absolute bottom-0 left-0 w-96 h-96 bg-white/5 rounded-full blur-3xl" data-id="element-754" />

            <div className="relative z-10" data-id="element-755">
              <h2 className="font-serif text-4xl md:text-5xl mb-6" data-id="element-756">
                Experience Our Space
              </h2>
              <p className="text-xl mb-10 text-white/90 max-w-2xl mx-auto" data-id="element-757">
                Take a virtual tour of our facilities or schedule an in-person
                visit to experience the tranquility firsthand
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-758">
                <button className="bg-white text-[#8B9D83] hover:bg-[#FAF7F2] px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300 transform hover:scale-105 shadow-xl" data-id="element-759">
                  Take Virtual Tour
                </button>
                <button className="bg-transparent border-2 border-white text-white hover:bg-white/10 px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300" data-id="element-760">
                  Schedule a Visit
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Gallery */}
      <section className="py-20 bg-[#FAF7F2]" data-id="element-761">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-762">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-763">
            <h2 className="font-serif text-4xl text-[#2C3E50] mb-12 text-center" data-id="element-764">
              Photo Gallery
            </h2>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-id="element-765">
              {['https://images.unsplash.com/photo-1544367563-12123d8965cd?q=80&w=2070&auto=format&fit=crop', 'https://images.unsplash.com/photo-1506126613408-eca07ce68773?q=80&w=2070&auto=format&fit=crop', 'https://images.unsplash.com/photo-1519834785169-98be25ec3f84?q=80&w=1964&auto=format&fit=crop', 'https://images.unsplash.com/photo-1528319725582-ddc096101511?q=80&w=2070&auto=format&fit=crop', 'https://images.unsplash.com/photo-1545389336-cf090694435e?q=80&w=2071&auto=format&fit=crop', 'https://images.unsplash.com/photo-1593811167562-9cef47bfc4d7?q=80&w=2072&auto=format&fit=crop', 'https://images.unsplash.com/photo-1518241353330-0f7941c2d9b5?q=80&w=2525&auto=format&fit=crop', 'https://images.unsplash.com/photo-1544161515-4ab6ce6db874?q=80&w=2070&auto=format&fit=crop'].map((image, index) => <motion.div key={index} initial={{
              opacity: 0,
              scale: 0.9
            }} whileInView={{
              opacity: 1,
              scale: 1
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: index * 0.05
            }} className="aspect-square rounded-xl overflow-hidden shadow-md group cursor-pointer" data-id="element-766">
                  <img src={image} alt={`Facility ${index + 1}`} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" data-id="element-767" />
                </motion.div>)}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Booking CTA */}
      <section className="py-20 bg-white" data-id="element-768">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-769">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-770">
            <h2 className="font-serif text-4xl text-[#2C3E50] mb-6" data-id="element-771">
              Ready to Begin Your Journey?
            </h2>
            <p className="text-lg text-[#5D6D7E] mb-10" data-id="element-772">
              Explore our upcoming courses and reserve your space in this
              transformative environment
            </p>
            <button className="bg-[#8B9D83] hover:bg-[#7A8C72] text-white px-12 py-4 rounded-full font-semibold text-lg transition-all duration-300 transform hover:scale-105 shadow-lg" data-id="element-773">
              View Upcoming Courses
            </button>
          </motion.div>
        </div>
      </section>

      <Footer data-id="element-774" />
    </div>;
}